import React, { useState } from "react";
import { Box, Typography, IconButton, Grid, Paper, Button } from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from "react-router-dom";
import DayEventsModal from "./DayEventsModal";
import EventDetailsModal from "./EventDetailsModal";
import { useTheme } from "../ThemeContext";
import { PRIMARY_PINK } from "../constants";

function getMonthMatrix(year, month) {
  // Returns a 6x7 matrix of Date objects for the calendar month view
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const prevMonthLastDay = new Date(year, month, 0);
  const matrix = [];
  let day = 1 - firstDay.getDay(); // Start from Sunday
  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      row.push(new Date(year, month, day));
      day++;
    }
    matrix.push(row);
  }
  return matrix;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Helper function to get date string in local timezone (YYYY-MM-DD)
function getLocalDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function eachLocalDateKeys(startValue, endValue) {
  const start = new Date(startValue);
  const end = new Date(endValue || startValue);
  const keys = [];
  let year = start.getFullYear();
  let month = start.getMonth();
  let day = start.getDate();
  const endYear = end.getFullYear();
  const endMonth = end.getMonth();
  const endDay = end.getDate();

  while (keys.length < 366) {
    const current = new Date(year, month, day);
    keys.push(getLocalDateString(current));
    if (year === endYear && month === endMonth && day === endDay) break;
    if (current > end) break;
    const next = new Date(year, month, day + 1);
    year = next.getFullYear();
    month = next.getMonth();
    day = next.getDate();
  }
  return keys;
}

function isCalendarMajor(item) {
  return item?.item_type === 'major' || (!item?.start_time && !!item?.start_date);
}

function CustomCalendar({ events = [], showEventTitles = false }) {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);

  const today = new Date();
  const matrix = getMonthMatrix(current.year, current.month);

  // Map events by date string (YYYY-MM-DD) in local timezone.
  // Majors appear on every day in their start/end range.
  const eventMap = {};
  events.forEach(ev => {
    const keys = isCalendarMajor(ev)
      ? eachLocalDateKeys(ev.start_date || ev.start_time, ev.end_date || ev.start_date || ev.start_time)
      : [getLocalDateString(new Date(ev.start_time || ev.start_date))];
    keys.forEach(key => {
      if (!eventMap[key]) eventMap[key] = [];
      eventMap[key].push(ev);
    });
  });

  const handlePrev = () => {
    setCurrent(c => {
      if (c.month === 0) return { year: c.year - 1, month: 11 };
      return { year: c.year, month: c.month - 1 };
    });
  };
  const handleNext = () => {
    setCurrent(c => {
      if (c.month === 11) return { year: c.year + 1, month: 0 };
      return { year: c.year, month: c.month + 1 };
    });
  };
  const handleToday = () => {
    setCurrent({ year: today.getFullYear(), month: today.getMonth() });
  };

  const openCalendarItem = (item) => {
    if (isCalendarMajor(item)) {
      navigate(`/majors/${item.id}`);
      return;
    }
    setSelectedEvent(item);
    setShowEventModal(true);
  };

  const handleDayClick = (date) => {
    const key = getLocalDateString(date);
    const dayEvents = eventMap[key] || [];
    
    if (dayEvents.length === 0) {
      return;
    } else if (dayEvents.length === 1) {
      openCalendarItem(dayEvents[0]);
    } else {
      setSelectedDayEvents(dayEvents);
      setSelectedDate(date);
      setShowDayEventsModal(true);
    }
  };

  const handleCloseDayEventsModal = () => {
    setShowDayEventsModal(false);
    setSelectedDayEvents([]);
    setSelectedDate(null);
  };

  const handleEventClick = (event) => {
    setShowDayEventsModal(false);
    openCalendarItem(event);
  };

  const handleCloseEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  // Theme-aware colors
  const calendarBg = darkMode ? '#2d2d2d' : '#f5f5f5';
  const dayCellBg = darkMode ? '#1e1e1e' : '#fff';
  const otherMonthBg = darkMode ? '#2a2a2a' : '#f0f0f0';
  const todayBg = darkMode ? '#1a3f5c' : '#e3f2fd';
  const borderColor = darkMode ? '#404040' : '#e0e0e0';
  const todayBorder = darkMode ? '#64b5f6' : '#1976d2';

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <IconButton onClick={handlePrev}><ArrowBackIosNewIcon /></IconButton>
          <IconButton onClick={handleNext}><ArrowForwardIosIcon /></IconButton>
          <Button onClick={handleToday} size="small" sx={{ ml: 1 }}>Today</Button>
        </Box>
        <Typography variant="h5" fontWeight="bold">
          {new Date(current.year, current.month).toLocaleString('default', { month: 'long', year: 'numeric' })}
        </Typography>
        <Box width={120} />
      </Box>
      <Grid container spacing={0.5} sx={{ background: calendarBg, borderRadius: 2, p: 1 }}>
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(day => (
          <Grid item xs={1.71} key={day}>
            <Typography align="center" fontWeight="bold">{day}</Typography>
          </Grid>
        ))}
        {matrix.flat().map((date, idx) => {
          const isCurrentMonth = date.getMonth() === current.month;
          const isToday = isSameDay(date, today);
          const key = getLocalDateString(date);
          const hasEvents = !!eventMap[key];
          return (
            <Grid item xs={1.71} key={key + idx}>
              <Paper
                elevation={isToday ? 4 : 1}
                sx={{
                  height: showEventTitles ? { xs: 110, sm: 128, md: 148 } : 64,
                  bgcolor: isCurrentMonth ? (isToday ? todayBg : dayCellBg) : otherMonthBg,
                  border: isToday ? `2px solid ${todayBorder}` : `1px solid ${borderColor}`,
                  cursor: isCurrentMonth && hasEvents ? 'pointer' : 'default',
                  opacity: isCurrentMonth ? 1 : 0.5,
                  p: 1,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.2s',
                  '&:hover': isCurrentMonth && hasEvents ? { boxShadow: 6 } : {},
                }}
                onClick={() => isCurrentMonth && hasEvents && handleDayClick(date)}
              >
                <Typography align="right" fontWeight={isToday ? 700 : 400} color={isToday ? 'primary' : 'textPrimary'}>
                  {date.getDate()}
                </Typography>
                {hasEvents && (
                  showEventTitles ? (
                    <Box sx={{ mt: 0.25, display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                      {eventMap[key].slice(0, 3).map(ev => (
                        <Box
                          key={`${ev.item_type || 'event'}-${ev.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(ev);
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            cursor: 'pointer',
                            '&:hover .calendar-event-title': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isCalendarMajor(ev) ? PRIMARY_PINK : '#1976d2', flexShrink: 0 }} />
                          <Typography
                            className="calendar-event-title"
                            noWrap
                            title={ev.title}
                            sx={{
                              fontSize: { xs: '0.65rem', sm: '0.72rem', md: '0.78rem' },
                              lineHeight: 1.25,
                              color: 'text.primary'
                            }}
                          >
                            {ev.title}
                          </Typography>
                        </Box>
                      ))}
                      {eventMap[key].length > 3 && (
                        <Typography variant="caption" sx={{ color: PRIMARY_PINK, pl: 1.5, fontSize: '0.7rem' }}>
                          +{eventMap[key].length - 3} more
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box position="absolute" left={8} bottom={8} display="flex" gap={0.5}>
                      {eventMap[key].slice(0,3).map(ev => (
                        <Box key={`${ev.item_type || 'event'}-${ev.id}`} width={8} height={8} borderRadius="50%" bgcolor={isCalendarMajor(ev) ? PRIMARY_PINK : "#1976d2"} />
                      ))}
                      {eventMap[key].length > 3 && (
                        <Typography variant="caption" sx={{ color: PRIMARY_PINK }}>+{eventMap[key].length - 3}</Typography>
                      )}
                    </Box>
                  )
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      
      <DayEventsModal
        open={showDayEventsModal}
        onClose={handleCloseDayEventsModal}
        events={selectedDayEvents}
        selectedDate={selectedDate}
        onEventClick={handleEventClick}
      />
      
      <EventDetailsModal
        open={showEventModal}
        onClose={handleCloseEventModal}
        event={selectedEvent}
        clubName={selectedEvent?.club_name || "Unknown Club"}
      />
    </Box>
  );
}

export default CustomCalendar; 